import type { GameEndReason } from '@chess-uno/shared-protocol'
import type { Messages } from './fr.js'

/** Plural of a counted word, English rule (only 1 is singular). */
function n(count: number, word: string, plural = `${word}s`): string {
  return `${count} ${count === 1 ? word : plural}`
}

export const en: Messages = {
  common: {
    back: 'Back',
    home: 'Home',
    cancel: 'Cancel',
    loading: 'Loading…',
    error: 'Error',
    yes: 'Yes',
    no: 'No',
    armies: { w: 'White', b: 'Black' },
    tiers: { bronze: 'Bronze', silver: 'Silver', gold: 'Gold', platinum: 'Platinum', diamond: 'Diamond' },
  },

  modes: {
    purist: {
      name: 'Purist',
      tagline: 'Checkmate',
      desc: 'Like chess: a king is never left in check, kings are never captured, you win by checkmate.',
      note: 'Purist mode: you may not leave your king in check; you win by checkmate.',
    },
    capture: {
      name: 'King Hunt',
      tagline: 'King capture',
      desc: 'The original rules: a king may be left attacked, and you win by capturing it.',
      note: 'King Hunt mode: a king may stay attacked; you win by capturing it.',
    },
  },

  campaign: {
    eyebrow: 'November challenge',
    title: 'Beat the machine',
    text: 'Beat the computer on Medium and share your win on Instagram: a €20 gift card is drawn every Monday.',
    link: 'All the details on Instagram',
    dismiss: 'Hide',
  },

  language: {
    names: { fr: 'Français', en: 'English', es: 'Español', de: 'Deutsch', hi: 'हिन्दी' },
    menu: 'Language',
    hint: 'This site is also available in English.',
    hintLink: 'English version',
    dismiss: 'Close',
  },

  chrome: {
    homeAria: 'Chess-Uno, home',
    rules: 'Rules',
    viewProfile: 'View my profile',
    soundOn: 'Turn sound on',
    soundOff: 'Turn sound off',
    logout: 'Sign out',
    legal: 'Legal notice',
    privacy: 'Privacy',
    instagram: 'Chess-Uno on Instagram',
    facebook: 'Chess-Uno on Facebook',
  },

  feedback: {
    link: 'Feedback',
    title: 'Tell us what you think',
    lead: 'An idea, a bug, a compliment? One message is enough: the publisher of the game reads it.',
    close: 'Close',
    kindLabel: 'Topic',
    kinds: { idea: 'Idea', bug: 'Bug', praise: 'Love it', other: 'Other' },
    messageLabel: 'Your message',
    placeholders: {
      idea: 'What would you like to find in Chess-Uno?',
      bug: 'What happened? On which page, on which device?',
      praise: 'What do you like the most?',
      other: 'Write whatever you want to tell us.',
    },
    contactLabel: 'So we can answer you (optional)',
    contactPlaceholder: 'Email or Telegram handle',
    fine: (privacy) => (
      <>
        Your message is read by the publisher, along with your username if you are signed in ({privacy('privacy policy')}).
      </>
    ),
    send: 'Send',
    sending: 'Sending…',
    thanksTitle: 'Thank you!',
    thanksText: 'Your message arrived. It helps improve the game.',
    rateGame: 'Did you enjoy this game?',
    starLabel: (count) => `${count} out of 5`,
    rated: 'Thanks for your rating!',
    tellMore: 'Tell us why',
  },

  toast: {
    aria: (name) => `Challenge from ${name}`,
    challengesYou: (name) => (
      <>
        <strong>{name}</strong> challenges you
      </>
    ),
    accept: 'Accept the duel',
    later: 'Later',
  },

  auth: {
    login: 'Sign in',
    register: 'Create an account',
    username: 'Username',
    password: 'Password',
    registerFine: (privacy) => (
      <>
        Username of 3 to 20 characters, password of at least 6 characters. Your username and results appear in the public ranking (
        {privacy('privacy')}).
      </>
    ),
    submitRegister: 'Create my account',
    submitLogin: 'Sign in',
  },

  landing: {
    eyebrow: 'Chess-Uno · chess and cards online',
    title: 'Play chess with cards.',
    titleEm: 'Every turn changes the game.',
    lead:
      'In Chess-Uno, you play chess with cards revealed as the game goes on. Every turn changes your options: number of moves, available pawns or the army you control. Win by checkmate, or by capturing the enemy king in King Hunt mode. Challenge a friend, find a ranked opponent or practice against the computer.',
    readRules: 'Read the rules',
    leaderboard: 'Ranking',
    howEyebrow: 'How to play',
    howTitle: 'A game of chess, with cards that change the moves',
    howP1:
      'The board and the pieces move as in chess. At the start of each turn, a card is drawn and revealed to everyone: it can grant several moves, skip a turn, give pawns back or swap the armies. Two modes: Purist, where you win by checkmate, and King Hunt, where you win by capturing the enemy king.',
    howP2:
      'You keep no cards in hand. A number card gives you as many moves as its value; you can stop that series after the first move. The +2 and +4 cards let you put captured pawns back on the board. With Reverse, the players swap armies without changing the position of the pieces. A Joker lets one piece move like a queen for the turn. Both players always see the revealed card.',
    discoverRules: 'Discover the rules of Chess-Uno',
    playEyebrow: 'Play online',
    playTitle: 'With friends, in ranked games or against the computer',
    playP1:
      'Create a private duel and share its link, join the queue for a ranked game with Elo, or try the cards against the computer. Games against the computer do not change your rating.',
    playP2:
      'Once you have an account, you can invite a friend with a private link or look for an opponent automatically. Finished games between two accounts update your Elo; the ranking also shows recent results and your head-to-head records. To discover the effects with nothing at stake, start a practice game against the computer. Before a private duel, you can choose which cards are in the deck and set the highest number card.',
    heroAlt: 'A chess pawn in front of cards',
    faqEyebrow: 'Frequently asked questions',
    faqTitle: 'Everything about Chess-Uno',
    faq: [
      {
        q: 'What is Chess-Uno?',
        a: 'Chess-Uno is a chess variant you can play for free online, in the browser. It uses a standard chessboard and the usual pieces, but at the start of each turn a card is drawn and revealed to both players: it sets what the player can do during that turn.',
      },
      {
        q: 'How do you win a game?',
        a: 'It depends on the mode chosen before the game. In Purist mode, as in chess, you may never leave your king in check and you win by checkmate: when the opponent cannot parry the check with the card they just drew. In King Hunt mode, you may leave your king attacked, and you win by capturing the enemy king.',
      },
      {
        q: 'What do the cards do?',
        a: 'A number card grants as many moves as its value (1 to 5 by default). Skip makes the player lose the turn. The +2 and +4 cards give back captured pawns to place on the board. Reverse swaps the two players’ armies. The Joker lets one piece move like a queen. No player keeps cards in hand.',
      },
      {
        q: 'Can I play alone?',
        a: 'Yes, against the computer, at Easy or Medium level. These practice games do not count for the ranking. You can also challenge a friend with a private link or find an opponent in the ranked queue.',
      },
      {
        q: 'How does the ranking work?',
        a: 'Every player starts at 1000 Elo. Only finished games between two accounts are ranked. Tiers go from Bronze to Diamond (1400 Elo and above), and head-to-head records between players are public.',
      },
      {
        q: 'Do I need to pay or install anything?',
        a: 'No. Chess-Uno is free and plays in the browser, on desktop and mobile. You only need to create an account with a username and a password, without an email address.',
      },
      {
        q: 'Is Chess-Uno related to the UNO game?',
        a: 'No. Chess-Uno is an independent game, not affiliated with, endorsed or sponsored by Mattel, owner of the UNO trademark.',
      },
    ],
  },

  home: {
    greeting: (hour) => (hour >= 5 && hour < 12 ? 'Good morning' : hour >= 12 && hour < 18 ? 'Good afternoon' : 'Good evening'),
    play: 'Play',
    ranked: 'Ranked game',
    rankedDetail: 'Opponent found automatically',
    challengeFriend: 'Challenge a friend',
    challengeFriendDetail: 'Private link to share',
    join: 'Join',
    joinDetail: 'With an invite code',
    training: 'Practice',
    trainingDetail: 'Against the AI, unranked',
    tournament: 'Tournament',
    tournamentDetail: 'Knockout, with a code',
    tournamentOngoing: (name) => `Tournament in progress: ${name}`,
    tournamentOpen: (name) => `Registration open: ${name}`,
    rules: 'Game rules',
    waitingGame: 'Game waiting for an opponent',
    ongoingAgainst: (name) => `Game in progress against ${name}`,
    resume: 'Resume',
    leaderboard: 'Overall ranking',
    recentResults: 'Latest results',
    headToHead: 'Head-to-head',
    rivalries: 'Great rivalries',
  },

  join: {
    eyebrow: 'Invitation',
    title: 'Join a game',
    lead: 'Enter the code your opponent sent you.',
    code: 'Invite code',
    submit: 'Join the game',
  },

  invite: {
    challengesYou: (name) => (
      <>
        <strong>{name}</strong> challenges you
      </>
    ),
    invited: 'You are invited to a duel',
  },

  gate: {
    title: 'Sign in',
    lead: 'Sign in to play and climb the ranking.',
  },

  queue: {
    searching: 'Looking for an opponent…',
    chooseTitle: 'Choose your mode',
    chooseLead: 'You will face a player who chose the same mode. The Elo rating is shared by both modes.',
    changeMode: 'Change mode',
    eyebrow: 'Ranked game',
    lead: 'The game starts as soon as a player joins the queue.',
  },

  competition: {
    justNow: 'just now',
    minutesAgo: (m) => `${m} min ago`,
    hoursAgo: (h) => `${h} h ago`,
    daysAgo: (d) => `${n(d, 'day')} ago`,
    tierTitle: (label) => `${label} tier`,
    streakTitle: (count) => `${n(count, 'win')} in a row`,
    form: { W: 'Win', L: 'Loss', D: 'Draw' },
    formAria: (items) => `Form: ${items.join(', ')}`,
    yourRanking: 'Your ranking',
    outOf: (count) => `of ${n(count, 'ranked player')}`,
    unranked: 'Unranked — play a ranked game',
    pointsFor: (missing, label) => `${missing} more points for ${label}`,
    maxTier: 'Top tier',
    pointsBefore: (missing, label) => (
      <>
        <b>{missing}</b> points to {label}
      </>
    ),
    maxTierCaption: 'Top tier — defend your place',
    wins: 'Wins',
    losses: 'Losses',
    draws: 'Draws',
    winRate: 'Win rate',
    percent: (value) => `${value}%`,
    streak: 'Streak',
    best: (count) => `best ${count}`,
    leaderboardEmpty: 'Nobody is ranked yet. Win a game against a friend to open the ranking.',
    you: 'you',
    record: (w, l, d) => `${w} W · ${l} L${d ? ` · ${d} D` : ''}`,
    challenge: 'Challenge',
    taunts: {
      never: 'Never played: open the score.',
      twoLosses: 'Two losses in a row. Time for revenge.',
      dominate: 'You dominate this duel.',
      lead: 'You lead, nothing is settled.',
      tied: 'Perfectly tied.',
      escape: 'This duel is slipping away.',
      behind: 'You are behind.',
    },
    rivalsEmpty: 'No head-to-head yet. Challenge a player from the ranking.',
    revenge: 'Revenge',
    replay: 'Play again',
    drawsShort: (d) => `${d} D`,
    rivalriesEmpty: 'A rivalry appears once two players have met twice.',
    rivalryMeta: (games, draws) => `${n(games, 'game')}${draws ? ` · ${n(draws, 'draw')}` : ''}`,
    recentEmpty: 'No ranked games yet.',
    and: 'and',
    drew: 'drew',
    beat: 'beat',
    reasons: {
      king_captured: 'king captured',
      checkmate: 'checkmate',
      resign: 'resignation',
      abandon: 'disconnection',
      draw_agreed: 'draw agreed',
      no_move_limit: 'draw by inactivity',
    },
  },

  settings: {
    decrease: 'Decrease',
    increase: 'Increase',
    maxNumberAria: 'Highest number card',
    keepCards: (max) => `Keep cards 1 to ${max}`,
    eyebrowBot: 'Practice · unranked',
    eyebrowPrivate: 'Private game · ranked',
    title: 'Game settings',
    leadBot: 'Set up the opponent and the deck, then start the game.',
    leadPrivate: 'Set up the deck; an invite link is created for your opponent.',
    opponent: 'Opponent',
    opponentHint: 'Experimental levels: the AI gets more or less thinking time.',
    easy: 'Easy',
    easyDesc: 'Looks one opponent move ahead.',
    medium: 'Medium',
    mediumDesc: 'Looks two moves ahead, thinks longer.',
    side: 'Your side',
    sideHint: 'A Reverse card can swap the armies during the game.',
    whiteDesc: 'You start',
    blackDesc: 'The AI starts',
    random: 'Random',
    randomSummary: 'Random draw',
    rules: 'Rules',
    rulesHint: 'Two ways to play: like chess, or hunting the king.',
    deck: 'Deck',
    deckHint: 'A number card grants as many moves as its value.',
    numberCards: 'Number cards',
    fromOneTo: (max) => (
      <>
        from 1 to <b>{max}</b>
      </>
    ),
    jokers: 'Jokers',
    jokersDesc: 'One move, where any piece moves like a queen.',
    advanced: 'Advanced rules',
    advancedHint: 'Pawn recovery, safeguard, draw.',
    plus2: 'Pawns returned by a +2',
    plus4: 'Pawns returned by a +4',
    drawAfter: 'Draw after N turns without a move',
    safeguard: 'Series safeguard',
    safeguardDesc: 'A series stops as soon as the enemy king is attacked.',
    summary: 'Summary',
    mode: 'Mode',
    modeBot: 'Against the AI',
    modePrivate: 'Private link',
    level: 'Level',
    camp: 'Side',
    deckSize: (count) => n(count, 'card'),
    numbers: 'Numbers',
    range: (max) => `1 to ${max}`,
    jokersIncluded: 'Jokers',
    ranking: 'Ranking',
    unranked: 'Unranked',
    eloAtStake: 'Elo at stake',
    start: 'Start the game',
    create: 'Create the game',
    locked: 'The rules are locked when the game starts.',
  },

  game: {
    drawAgreed: 'Draw (mutual agreement).',
    drawIdle: 'Draw (turns without a move).',
    result: (mine, winner, reason) => {
      const why: Partial<Record<GameEndReason, string>> = {
        king_captured: 'capturing the king',
        checkmate: 'checkmate',
        resign: 'resignation',
        abandon: 'prolonged disconnection',
      }
      return `${mine ? 'Victory' : 'Defeat'} — ${winner} wins by ${why[reason] ?? reason}.`
    },
    headToHead: 'Head-to-head',
    firstDuel: 'First duel',
    youLead: 'You lead',
    leads: (name) => `${name} leads`,
    tied: 'Tied',
    training: (level) => `Practice game against the AI (${level} level): unranked.`,
    computing: 'Computing the ranking…',
    promotion: (tier) => `Promoted: ${tier} tier`,
    demotion: (tier) => `Relegated to ${tier}… time for revenge.`,
    recordAgainst: (name) => `Record against ${name}:`,
    streak: (count) => `${n(count, 'win')} in a row`,
    backHome: 'Back to home',
    privateGame: 'Private game',
    waiting: 'Waiting for an opponent…',
    shareLink: 'Share this link:',
    copied: 'Copied!',
    copyLink: 'Copy the link',
    inviteText: (name) => `${name} challenges you on Chess-Uno! Accept the duel:`,
    messageCopied: 'Message copied!',
    sent: 'Sent!',
    sendChallenge: 'Send the challenge',
    code: 'Code:',
    notified: 'The challenged player also gets a notification if they are online.',
    loadingGame: 'Loading the game…',
    connecting: '(connecting to the server…)',
    connected: 'Connected',
    reconnecting: 'Reconnecting…',
    acceptDraw: 'Accept the draw',
    decline: 'Decline',
    offerDraw: 'Offer a draw',
    offerDrawShort: 'Draw',
    offerDrawConfirm: 'Offer your opponent a draw?',
    acceptDrawShort: 'Accept',
    resignConfirm: 'Resign the game?',
    resign: 'Resign',
    resultAria: 'Result',
    rematchOffered: (name) => `${name} offers a rematch.`,
    rematchWaiting: 'Rematch offered: waiting for the opponent…',
    rematch: 'Rematch',
    closeResult: 'Close to see the board',
    showResult: 'Show the result',
    verdicts: { win: 'Victory', loss: 'Defeat', draw: 'Draw' },
    winText: (opponent, gain, rating) => `I beat ${opponent} on Chess-Uno (+${gain} Elo, ${rating}). Who’s next?`,
    shared: 'Shared!',
    shareWin: 'Share my win',
  },

  panels: {
    phases: {
      turn_start: 'Start of turn',
      draw: 'Draw',
      effect_choice: 'Choosing the effect (Joker)',
      place_pawns: 'Placing recovered pawns',
      moves: 'Moves',
      turn_end: 'End of turn',
      game_over: 'Game over',
    },
    playsArmy: (army) => `Plays ${army === 'w' ? 'White' : 'Black'}`,
    chooseMoves: (you) => (you ? 'Choose your number of moves' : 'is choosing their number of moves'),
    placePawns: (you, count) => (you ? `Place ${n(count, 'pawn')} (green squares)` : `is placing ${n(count, 'pawn')}`),
    joker: (you) => (you ? 'Joker: move any piece like a queen' : 'Joker: moves a piece like a queen'),
    moreMoves: (you, count) => (you ? `${n(count, 'more move')} (or end the series)` : `${n(count, 'more move')}`),
    playMoves: (you, count) => (you ? `Play ${n(count, 'move')}` : `plays ${n(count, 'move')}`),
    pipsAria: (made, total) => `${made} of ${n(total, 'move')} played`,
    yourTurn: 'Your turn',
    turnOf: (name) => `${name}’s turn`,
    check: 'Check!',
    go: 'Go!',
    gameStarts: 'The game begins',
    drawing: (you, summary) => `${you ? 'You draw' : 'draws'}: ${summary}`,
    inCheckYou: (mode) => (mode === 'purist' ? 'Your king is in check: parry with your card.' : 'Your king is attacked: parry with your card.'),
    inCheckOpponent: (mode) =>
      mode === 'purist' ? 'Enemy king in check: without a parry, it’s checkmate.' : 'Enemy king attacked: without a parry, it will be captured.',
    turnNumber: (turn) => `Turn ${turn}`,
    discardTitle: (owner, count) => `${owner}’s discard pile: ${n(count, 'card')}`,
    discard: 'Discard',
    seatAria: (name) => `Player ${name}`,
    connected: 'Connected',
    disconnected: 'Disconnected',
    you: '(you)',
    ai: 'AI',
    eloTitle: (tier) => `Elo rating · ${tier}`,
    seatYourTurn: 'your turn',
    seatPlaying: 'playing',
    pawnsTitle: 'Captured pawns that a +2 or a +4 can recover',
    toRecover: (count) => `${count} to recover`,
    turnCardAria: 'Turn card',
    drawPile: 'Draw pile',
    youDrew: 'You drew',
    drew: (name) => `${name} drew`,
    revealed: (label) => `Revealed: ${label}`,
    noCard: 'No card',
    jokerChoiceAria: 'Choosing the number of moves',
    jokerChoice: 'Joker: how many moves this turn?',
    placementHint: (count) => `Click a green square on the board to place a pawn (${count} left).`,
    endSeries: (count) => `End the series (${n(count, 'move')} not played)`,
    effects: {
      number: (count) => `${n(count, 'move')} to play.`,
      zero: 'Turn skipped without moving.',
      skip: 'Skip: the opponent immediately draws and plays their card.',
      reverse: 'Reverse: the players swap armies; the current player continues with the new one.',
      plus: (card, pawns, moves) => `${card}: recovers up to ${pawns} captured pawns, then ${n(moves, 'move')}.`,
      wild: 'Joker: one move, with any piece moving like a queen.',
    },
    reveal: {
      who: (you, name) => (you ? 'You draw' : `${name} draws`),
      moves: (count) => n(count, 'move'),
      zero: 'Turn skipped',
      zeroDetail: 'No move this turn.',
      skip: 'Loses the turn',
      skipDetail: (name) => `${name} draws and plays.`,
      reverse: 'Reverse!',
      reverseDetail: 'The players swap armies.',
      recovers: (count) => `Recovers ${n(count, 'pawn')}`,
      then: (count) => `Then ${n(count, 'move')}.`,
      oneMove: '1 move',
      noPawn: 'No pawn to recover.',
      joker: 'Joker',
      jokerDetail: 'Any piece of your choice moves like a queen.',
    },
    yourKingAttacked: 'Your king is attacked',
    enemyKingAttacked: 'The enemy king is attacked',
    youWin: 'You win',
    wins: (name) => `${name} wins`,
    pieces: { p: 'the pawn', n: 'the knight', b: 'the bishop', r: 'the rook', q: 'the queen', k: 'the king' },
    somePiece: 'the piece',
    deliversMate: (piece, from) => `${piece} on ${from} delivers checkmate`,
    takesKing: (piece, from, to) => `${piece} on ${from} captures the king on ${to}`,
    kingTakenOn: (square) => `The king is captured on ${square}`,
    checkmate: 'Checkmate',
    kingCaptured: 'King captured',
    statusAria: 'Game status',
    phase: 'Phase',
    actor: 'Player to act',
    movesRemaining: 'Moves left',
    movesValue: (remaining, made) => `${remaining} (played: ${made})`,
    idleTurns: 'Turns without a move',
    carefulKing: 'Careful king: the king cannot move to an attacked square.',
    kingAttacked: (army, mode) =>
      mode === 'purist'
        ? `The ${army === 'w' ? 'white' : 'black'} king is in check.`
        : `The ${army === 'w' ? 'white' : 'black'} king is attacked (information only, no reply is required).`,
    youOfferedDraw: 'You offered a draw.',
    offersDraw: (name) => `${name} offers a draw.`,
    log: 'Log',
  },

  board: {
    promotionAria: 'Promotion choice',
    promotion: 'Promotion:',
    pieces: { q: 'Queen', r: 'Rook', b: 'Bishop', n: 'Knight' },
  },

  card: {
    back: 'Card back',
  },

  tournament: {
    eyebrow: 'Tournament',
    hubTitle: 'Knockout tournament',
    hubLead: 'Every defeat eliminates you: the last player standing wins the tournament.',
    joinTitle: 'Join a tournament',
    joinCode: 'Tournament code',
    joinSubmit: 'Join',
    createTitle: 'Host a tournament',
    createHint: 'You get a code to share. You start the tournament once everybody is here.',
    name: 'Name (optional)',
    namePlaceholder: 'Friday night cup',
    modeLabel: 'Game mode',
    sizeLabel: 'Maximum number of players',
    create: 'Create the tournament',
    mine: 'Your tournaments',
    back: 'Tournaments',
    defaultName: (host) => `${host}’s tournament`,
    status: { registration: 'Registration open', running: 'In progress', finished: 'Finished', cancelled: 'Cancelled' },
    playerCount: (count, max) => `${count} / ${max} players`,
    hostedBy: (name) => `Hosted by ${name}`,
    open: 'Open',
    codeLabel: 'Code',
    copyLink: 'Copy the link',
    copied: 'Copied!',
    share: 'Share',
    shared: 'Shared!',
    shareText: (name, host) => `${host} invites you to the tournament “${name}” on Chess-Uno. Join it:`,
    players: 'Players',
    host: 'Host',
    you: 'you',
    join: 'Join the tournament',
    leave: 'Leave the tournament',
    start: 'Start the tournament',
    startHint: 'At least 2 players are needed. The bracket is drawn at random when you start; nobody can sign up after that.',
    waitingHost: 'Waiting for the host to start…',
    cancel: 'Cancel the tournament',
    cancelConfirm: 'Cancel this tournament for all players?',
    yourMatch: (opponent) => `Your match is ready against ${opponent}`,
    play: 'Play',
    waitingNext: 'Next match: waiting for the other results…',
    byeThrough: 'You go through to the next round without playing.',
    eliminated: (round) => `Eliminated in the ${round.toLowerCase()}. Follow the rest here.`,
    champion: 'Tournament winner',
    youWon: 'Congratulations, you win the tournament!',
    cancelled: 'This tournament was cancelled by the host.',
    bracket: 'Bracket',
    roundName: (fromEnd) =>
      ({ 1: 'Final', 2: 'Semi-finals', 3: 'Quarter-finals', 4: 'Round of 16', 5: 'Round of 32' })[fromEnd] ?? `Round ${fromEnd}`,
    roundOne: (fromEnd) =>
      ({ 1: 'Final', 2: 'Semi-final', 3: 'Quarter-final', 4: 'Round of 16', 5: 'Round of 32' })[fromEnd] ?? `Round ${fromEnd}`,
    tbd: 'To be decided',
    bye: 'Goes through',
    matchStates: { waiting: 'Waiting', playing: 'In progress', done: 'Finished' },
    endedBy: {
      king_captured: 'king captured',
      checkmate: 'checkmate',
      resign: 'resignation',
      abandon: 'forfeit',
      draw_agreed: 'draw agreed',
      no_move_limit: 'draw by inactivity',
      coin_toss: 'coin toss',
    },
    redraws: (count) => `${n(count, 'draw')} replayed`,
    rulesTitle: 'Tournament rules',
    rules: (idleMinutes) => [
      'Knockout: the loser of a match is eliminated, the winner moves on to the next round.',
      'The bracket is drawn at random when the tournament starts. If the number of players is not a power of two, some go through the first round without playing.',
      'A drawn game is replayed with the colors swapped; after three draws, a coin toss decides.',
      `No rematch. A player who does not act for ${n(idleMinutes, 'minute')}, or who stays disconnected, loses the game.`,
      'Tournament games count for the Elo ranking.',
    ],
    notFoundTitle: 'Tournament not found',
    notFoundLead: 'Check the code you got from the host.',
    invited: 'You are invited to a tournament',
    invitedTo: (name) => (
      <>
        You are invited to the tournament <strong>{name}</strong>
      </>
    ),
    toast: {
      aria: (name) => `Tournament match: ${name}`,
      ready: (opponent) => (
        <>
          Your match is ready against <strong>{opponent}</strong>
        </>
      ),
      play: 'Play',
      later: 'Later',
    },
    game: {
      strip: (name, round) => `${name} · ${round}`,
      rule: (idleMinutes) => `No rematch · ${n(idleMinutes, 'minute')} of inactivity at most`,
      bracket: 'View the bracket',
      backToBracket: 'Back to the bracket',
      drawReplay: 'Draw: the match is replayed with the colors swapped.',
    },
  },

  profile: {
    notFound: 'Player not found.',
    eyebrow: 'Profile',
    challenge: (name) => `Challenge ${name}`,
    shareMe: (rank, rating) => `I’m ${rank ? `#${rank} ` : ''}on Chess-Uno with ${rating} Elo. Come and dethrone me!`,
    shareOther: (name, rating) => `${name} has ${rating} Elo on Chess-Uno. Who will take up the challenge?`,
    linkCopied: 'Link copied!',
    shared: 'Shared!',
    share: 'Share',
    yourRanking: 'Your ranking',
    ranking: 'Ranking',
    youVs: (name) => `You vs ${name}`,
    draws: (count) => n(count, 'draw'),
    headToHead: 'Head-to-head',
    recentResults: 'Latest results',
  },

  rules: {
    eyebrow: 'Rulebook',
    toc: 'Contents',
  },

  legal: {
    updated: (date) => `Last updated: ${date}`,
  },
}
